import axios from 'axios';
import { 
  BASE_URL, 
  AUTH_LOGIN, 
  AUTH_LOGOUT, 
  AUTH_REGISTER, 
  AUTH_CHANGE_PASSWORD, 
  USER_PROFILE,
  CSRF_TOKEN,
  CSRF_FALLBACK
} from '../config/Config';
import Cookies from 'js-cookie';

// Ensure the base API URL always ends with `/api`
let base = BASE_URL || '';
if (base && !base.endsWith('/')) base += '/';
export const API_URL = base ? (base.endsWith('api/') ? base.slice(0, -1) : (base + 'api')) : '/api';

// Create axios instance with default config for Token authentication
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,  // Required for cookies/sessions
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  crossDomain: true,
  validateStatus: function (status) {
    // Only treat 2xx status codes as successful responses
    return status >= 200 && status < 300;
  }
});

// Configure default headers
api.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
api.defaults.withCredentials = true; // Important for sending cookies with cross-origin requests

// Function to get CSRF token from cookies or meta tags
const getCSRFToken = () => {
  try {
    // First try to get from cookies
    let token = Cookies.get('csrftoken');
    
    // If not in cookies, try to get it from meta tags (common in Django)
    if (!token) {
      const csrfMeta = document.querySelector('meta[name="csrf-token"]');
      if (csrfMeta) {
        token = csrfMeta.getAttribute('content');
        if (token) {
          console.log('[API] Retrieved CSRF token from meta tag');
        }
      }
    } else {
      console.log('[API] Retrieved CSRF token from cookie');
    }
    
    return token || null;
  } catch (error) {
    console.error('[API] Error getting CSRF token:', error);
    return null;
  }
};

// Function to ensure we have a valid CSRF token
const ensureCSRFToken = async () => {
  // First try to get existing token
  let token = getCSRFToken();
  
  // If we have a token, return it
  if (token) {
    console.log('[API] Using existing CSRF token');
    return token;
  }
  
  console.log('[API] No CSRF token found, fetching a new one...');
  
  try {
    // Try the class-based view endpoint first
    const response = await axios({
      method: 'get',
      url: `${API_URL}${CSRF_TOKEN}`,
      withCredentials: true,
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      validateStatus: (status) => status < 500  // Don't throw on 4xx errors
    });
    
    // Check if we got a token in the response
    if (response.data?.csrfToken) {
      token = response.data.csrfToken;
      console.log('[API] Got CSRF token from API response');
    } else {
      // Try the function-based view endpoint as fallback
      console.log('[API] Trying fallback CSRF token endpoint...');
      const fallbackResponse = await axios({
        method: 'get',
        url: `${API_URL}${CSRF_FALLBACK}`,
        withCredentials: true,
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        validateStatus: (status) => status < 500
      });
      
      if (fallbackResponse.data?.csrfToken) {
        token = fallbackResponse.data.csrfToken;
        console.log('[API] Got CSRF token from fallback endpoint');
      }
    }
    
    // If we got a token, store it in cookies for future requests
    if (token) {
      // Set the cookie with proper attributes
      const secureFlag = window.location.protocol === 'https:' ? 'Secure;' : '';
      document.cookie = `csrftoken=${token}; Path=/; SameSite=Lax; ${secureFlag}`;
      console.log('[API] CSRF token stored in cookies');
    } else {
      // Last resort: check if the cookie was set by the server
      token = getCSRFToken();
      if (token) {
        console.log('[API] Retrieved CSRF token from cookie after request');
      } else {
        throw new Error('No CSRF token in response from server');
      }
    }
    
    return token;
    
  } catch (error) {
    console.error('[API] Error fetching CSRF token:', error);
    
    // If we have a response with status, log it
    if (error.response) {
      console.error('[API] CSRF token fetch failed with status:', error.response.status);
      console.error('[API] Response data:', error.response.data);
      
      // If it's a 403, it might be a CSRF validation error
      if (error.response.status === 403) {
        console.error('[API] CSRF validation failed - this might indicate a session issue');
      }
    }
    
    // If we're in development, provide more detailed error information
    if (process.env.NODE_ENV === 'development') {
      console.error('[API] CSRF token fetch error details:', {
        message: error.message,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          withCredentials: error.config?.withCredentials,
          headers: error.config?.headers
        },
        response: error.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          headers: error.response.headers,
          data: error.response.data
        } : 'No response'
      });
    }
    
    throw new Error('Failed to retrieve CSRF token. Please refresh the page and try again.');
  }
};

// Add a response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    }
    return response;
  },
  async (error) => {
    if (!error.response) {
      // Network error or server not reachable
      console.error('[API] Network error:', error.message);
      return Promise.reject({
        message: 'Network error. Please check your connection and try again.',
        isNetworkError: true,
        originalError: error
      });
    }

    const { status, config: originalRequest } = error.response;
    
    // Handle 401 Unauthorized (token expired or invalid)
    if (status === 401) {
      // If we've already retried, don't try again
      if (originalRequest._retry) {
        console.log('[API] Already retried authentication, redirecting to login');
        // Clear auth state and redirect to login
        localStorage.removeItem('authToken');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }
      
      // Mark that we're retrying this request
      originalRequest._retry = true;
      
      try {
        console.log('[API] Session may have expired, attempting to refresh CSRF token...');
        await ensureCSRFToken();
        
        // Get the new CSRF token
        const token = getCSRFToken();
        if (token) {
          originalRequest.headers['X-CSRFToken'] = token;
        }
        
        // Retry the original request with the new token
        console.log('[API] Retrying original request with new CSRF token');
        return api(originalRequest);
        
      } catch (refreshError) {
        console.error('[API] Failed to refresh session:', refreshError);
        // Clear auth state and redirect to login
        localStorage.removeItem('authToken');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject({
          message: 'Your session has expired. Please log in again.',
          isAuthError: true,
          originalError: refreshError
        });
      }
    }
    
    // Handle 403 Forbidden (CSRF token missing or incorrect)
    if (status === 403) {
      const data = error.response.data || {};
      if (data.detail === 'CSRF Failed: CSRF token missing or incorrect.') {
        console.warn('[API] CSRF token validation failed, attempting to refresh...');
        
        try {
          // Get a new CSRF token
          await ensureCSRFToken();
          
          // Update the original request with the new token
          const newToken = getCSRFToken();
          if (newToken) {
            originalRequest.headers['X-CSRFToken'] = newToken;
            console.log('[API] Retrying request with new CSRF token');
            return api(originalRequest);
          }
        } catch (e) {
          console.error('[API] Failed to refresh CSRF token:', e);
        }
        
        // If we get here, CSRF refresh failed
        return Promise.reject({
          message: 'Session security error. Please refresh the page and try again.',
          isSecurityError: true,
          originalError: error
        });
      }
      
      return Promise.reject({
        message: 'Access denied. You do not have permission to perform this action.',
        isAuthError: true,
        originalError: error
      });
    }
    
    // For other errors, just pass them through
    return Promise.reject(error);
  }
);

// Single request interceptor: CSRF, Authorization, Content-Type. No logging of headers or body (security).
api.interceptors.request.use(
  async (config) => {
    const method = (config.method || 'get').toUpperCase();
    const isSafeMethod = ['GET', 'HEAD', 'OPTIONS'].includes(method);
    const isCsrfEndpoint = (config.url || '').endsWith('/csrf/');

    if (!isSafeMethod && !isCsrfEndpoint && config.url?.startsWith(API_URL)) {
      try {
        const csrfToken = await ensureCSRFToken();
        if (csrfToken) {
          config.headers['X-CSRFToken'] = csrfToken;
        }
      } catch (error) {
        console.error('[API] CSRF token error:', error.message);
      }
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
    }

    const authToken = localStorage.getItem('token') || sessionStorage.getItem('token') || (axios.defaults.headers.common['Authorization'] || '').replace('Token ', '');
    if (authToken) {
      config.headers.Authorization = `Token ${authToken}`;
    }

    if (method !== 'GET' && method !== 'HEAD') {
      if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
      } else if (config.data && typeof config.data === 'object') {
        config.headers['Content-Type'] = 'application/json';
      }
    }

    return config;
  },
  (error) => {
    console.error('[API] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor: log only method + url + status (no bodies/headers to avoid leaking tokens/PII)
api.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('[API] Response error:', error.response.status, error.config?.method, error.config?.url);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('[API] No response received:', {
        url: error.config.url,
        method: error.config.method,
        message: error.message
      });
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('[API] Request setup error:', error.message);
    }
    return Promise.reject(error);
  }
);

// API functions
export const authAPI = {
  login: (credentials) => api.post(AUTH_LOGIN, credentials),
  register: (userData) => api.post(AUTH_REGISTER, userData),
  logout: () => api.post(AUTH_LOGOUT),
  changePassword: (passwordData) => api.post(AUTH_CHANGE_PASSWORD, {
    old_password: passwordData.oldPassword,
    new_password: passwordData.newPassword,
    confirm_password: passwordData.confirmPassword
  }),
  getProfile: () => api.get(USER_PROFILE),
  updateProfile: (formData) => {
    const headers = { 'Content-Type': 'multipart/form-data' };
    return api.patch(USER_PROFILE, formData, { headers });
  },
};

export default api;
